import { catalogoMaestrosApi } from "@/plugins/axios";
import type { AxiosResponse } from "axios";
import type {
  EstadoNegociacion,
  Laboratorio,
  Producto,
  Sociedad,
  Usuario,
} from "./types";

export interface MasterFilters {
  search?: string;
  correosAExcluir?: string[];
  page?: number | string;
  limit?: number | string;
}

interface PathParams {
  laboratorioNit?: string;
}

interface MasterArguments {
  filters?: MasterFilters;
  signal?: AbortSignal;
}

const buildParams = (filters: MasterFilters = {}) => {
  const params = new URLSearchParams();
  if (filters.search) {
    params.append("search", filters.search);
  }
  if (filters.correosAExcluir) {
    filters.correosAExcluir.forEach((correo) => {
      params.append("correosAExcluir", correo);
    });
  }
  if (filters.page !== undefined) {
    params.append("page", String(filters.page));
  }
  if (filters.limit !== undefined) {
    params.append("limit", String(filters.limit));
  }
  return params.toString();
};

export const maestrosDataService = {
  getSociedades: async ({
    filters = {},
    signal,
  }: MasterArguments): Promise<Sociedad[]> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/sociedades?${buildParams(filters)}`,
      { signal }
    );
    return response.data;
  },

  getProductos: async (
    {
      filters = {},
      signal,
    }: MasterArguments = {},
    pathParams: PathParams = {}
  ): Promise<{
    data: Producto[];
    total: number;
    page: number;
    limit: number;
  }> => {
    if (pathParams.laboratorioNit) {
      const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
        `/catalogo-ofima/laboratorios/${pathParams.laboratorioNit}/productos?${buildParams(filters)}`,
        { signal }
      );
      return response.data;
    }

    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/catalogo-ofima/productos?${buildParams(filters)}`,
      { signal }
    );
    return response.data;
  },

  getLaboratorios: async ({
    filters = {},
    signal,
  }: MasterArguments = {}): Promise<{ data: Laboratorio[] }> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/catalogo-ofima/laboratorios?${buildParams(filters)}`,
      { signal }
    );
    return response.data;
  },

  getAprobadores: async ({
    filters = {},
    signal,
  }: MasterArguments): Promise<{ data: Usuario[] }> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/usuarios/aprobadores?${buildParams(filters)}`,
      { signal }
    );
    return response.data;
  },

  getUsuarios: async ({ signal }: MasterArguments = {}): Promise<Usuario[]> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/usuarios`,
      { signal }
    );
    return response.data;
  },

  getEstadosNegociacion: async ({ signal }: MasterArguments = {}): Promise<
    EstadoNegociacion[]
  > => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/estado-negociacion`,
      { signal }
    );
    return response.data;
  },

  getGrupos: async ({ signal }: MasterArguments = {}): Promise<
    EstadoNegociacion[]
  > => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/grupo-descuentos`,
      { signal }
    );
    return response.data;
  },

  getEscalas: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/escalas`,
      { signal }
    );
    return response.data;
  },

  getDescuento: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/descuentos`,
      { signal }
    );
    return response.data;
  },

  getTipoDescuento: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/tipo-descuentos`,
      { signal }
    );
    return response.data;
  },

  getGrupoDescuentos: async ({ signal }: MasterArguments = {}): Promise<
    EstadoNegociacion[]
  > => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/grupo-descuentos`,
      { signal }
    );
    return response.data;
  },

  getBase: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/bases`,
      { signal }
    );
    return response.data;
  },
  getGrupoDescuento: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/grupo-descuentos`,
      { signal }
    );
    return response.data;
  },
  getBeneficiario: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/beneficiarios`,
      { signal }
    );
    return response.data;
  },
  getTipoBase: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/tipo-bases`,
      { signal }
    );
    return response.data;
  },
  getTipoRechazos: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/tipo-rechazos`,
      { signal }
    );
    return response.data;
  },
  getPeriodos: async ({ signal }: MasterArguments): Promise<any> => {
    const response: AxiosResponse<any, any> = await catalogoMaestrosApi.get(
      `/periodos`,
      { signal }
    );
    return response.data;
  },
};
